const baseUrl = () => {
  const configured = process.env.REACT_APP_SSO_BASE_URL;
  const fallback = process.env.REACT_APP_GQL_URL;
  if (!configured && !fallback) throw new Error("SmartMatrix service is not configured.");
  return configured ? new URL(configured).href.replace(/\/$/, "") : new URL(fallback!).origin;
};

export const smartMatrixUrl = (path: string) => `${baseUrl()}/api/auth/smartmatrix/${path}`;
export const smartMatrixHeaders = (): Record<string, string> => ({
  "X-SmartMatrix-SSO": "1",
  ...(new URL(smartMatrixUrl("start")).hostname.includes("ngrok") ? { "ngrok-skip-browser-warning": "1" } : {}),
});
export const startSmartMatrixLogin = () => {
  window.location.assign(smartMatrixUrl("start"));
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export async function smartMatrixJson(response: Response): Promise<Record<string, unknown>> {
  const body: unknown = await response.json().catch(() => null);
  if (!isRecord(body)) throw new Error("SmartMatrix returned an invalid response.");
  if (!response.ok) throw new SmartMatrixError(typeof body.error === "string" ? body.error : "SmartMatrix is unavailable. Please try again.", response.status, typeof body.code === "string" ? body.code : "", typeof body.retryAfterSeconds === "number" && Number.isFinite(body.retryAfterSeconds) ? Math.max(0, body.retryAfterSeconds) : 0);
  return body;
}

export class SmartMatrixError extends Error {
  constructor(message: string, public status = 0, public code = "", public retryAfterSeconds = 0) { super(message); Object.setPrototypeOf(this, SmartMatrixError.prototype); }
}
export const smartMatrixErrorText = (error: unknown) => error instanceof SmartMatrixError
  ? `${error.message}${error.code ? ` (Support code: ${error.code})` : ""}`
  : "SmartMatrix is unavailable. Please try again or contact support.";
export const existingAccountPath = () => localStorage.getItem(process.env.REACT_APP_LOCAL_TOKEN || "")
  ? "/auth/smartmatrix/link?start=1" : "/auth/login?smartmatrixLink=1";
export const isExistingAccountConflict = (error: unknown) => error instanceof SmartMatrixError && error.status === 409 &&
  ["EXISTING_ACCOUNT_LINK_REQUIRED", "FIRST_VISIT_ACCOUNT_AVAILABILITY_FAILED"].includes(error.code);
export const localReturnTo = (value: unknown) => {
  if (typeof value !== "string" || !value.startsWith("/") || value.startsWith("//") || /[\\\x00-\x20]/.test(value)) return "/profile";
  try { return new URL(value, window.location.origin).origin === window.location.origin ? value : "/profile"; }
  catch (_) { return "/profile"; }
};
export const smartMatrixConfigured = () => { try { smartMatrixUrl("start"); return true; } catch (_) { return false; } };
export async function smartMatrixLinkRequest(path: "start" | "status" | "send-otp" | "complete", token: string, otp?: string) {
  if (!token) throw new SmartMatrixError("Your institute session is unavailable. Restart linking from your profile.", 403);
  return smartMatrixJson(await fetch(smartMatrixUrl(`link/${path}`), {
    method: path === "status" ? "GET" : "POST", credentials: "include", cache: "no-store",
    headers: { ...smartMatrixHeaders(), Authorization: `Bearer ${token.replace(/^Bearer\s+/i, "")}`, ...(otp === undefined ? {} : { "Content-Type": "application/json" }) },
    ...(otp === undefined ? {} : { body: JSON.stringify({ otp }) }),
  }));
}
export function assignLinkAuthorization(value: unknown) {
  if (typeof value !== "string") throw new Error("Invalid authorization URL");
  const url = new URL(value);
  if (url.protocol !== "https:" && !(url.protocol === "http:" && ["localhost", "127.0.0.1"].includes(url.hostname))) throw new Error("Invalid authorization URL");
  window.location.assign(url.href);
}
